import { useState, useEffect } from "react";
import { Plus, Copy, Check, Key, Pencil, Trash2 } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { SettingsTable } from "@/components/v2/settings-table";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  useApiKeys,
  useCreateApiKey,
  useUpdateApiKey,
  useRevokeApiKey,
  type ApiKey,
} from "@/hooks/use-api-keys";
import { useAbility } from "@/hooks/use-ability";

// ─── Scope Groups ─────────────────────────────────────────────────────────────

type ScopeGroup =
  | {
      key: string;
      label: string;
      description: string;
      kind: "rw";
      readScope: string;
      writeScope: string;
      adminOnly?: boolean;
    }
  | {
      key: string;
      label: string;
      description: string;
      kind: "single";
      scope: string;
      adminOnly?: boolean;
    };

const SCOPE_GROUPS: ScopeGroup[] = [
  {
    key: "core",
    label: "Core",
    description: "Customers, contacts, products, locations",
    kind: "rw",
    readScope: "core:read",
    writeScope: "core:write",
  },
  {
    key: "sales",
    label: "Sales",
    description: "Proposals and opportunities",
    kind: "rw",
    readScope: "sales:read",
    writeScope: "sales:write",
  },
  {
    key: "ops",
    label: "Operations",
    description: "Jobs and work orders",
    kind: "rw",
    readScope: "ops:read",
    writeScope: "ops:write",
  },
  {
    key: "finance",
    label: "Finance",
    description: "Invoices and commissions",
    kind: "rw",
    readScope: "finance:read",
    writeScope: "finance:write",
  },
  {
    key: "import",
    label: "Data Import",
    description: "Ingest data via /import endpoints",
    kind: "single",
    scope: "import",
  },
  {
    key: "admin",
    label: "Admin",
    description: "Org settings and user management",
    kind: "rw",
    readScope: "admin:read",
    writeScope: "admin:write",
    adminOnly: true,
  },
  // ADV-998 — Inbound webhook keys are minted with a separate `adva_inb_*`
  // prefix and pinned to this scope. Selecting it triggers `kind: 'inbound'`
  // on the create request and disables the other scope toggles.
  {
    key: "inbound",
    label: "Inbound Webhook",
    description:
      "Catch-all inbound webhook (Zapier, Make, n8n, custom integrations)",
    kind: "single",
    scope: "inbound:write",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── Create API Key Sheet ─────────────────────────────────────────────────────

interface CreateApiKeySheetProps {
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
}

function CreateApiKeySheet({ open, onClose, isAdmin }: CreateApiKeySheetProps) {
  const [name, setName] = useState("");
  const [useWildcard, setUseWildcard] = useState(true);
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [expires, setExpires] = useState("");
  const [createdRawKey, setCreatedRawKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useCreateApiKey();
  const visibleGroups = SCOPE_GROUPS.filter((g) => !g.adminOnly || isAdmin);

  function handleClose() {
    setName("");
    setUseWildcard(true);
    setSelectedScopes(new Set());
    setExpires("");
    setCreatedRawKey(null);
    setError(null);
    onClose();
  }

  function toggleScope(scope: string) {
    setUseWildcard(false);
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }

  async function handleSubmit() {
    setError(null);
    const scopes = useWildcard ? ["*"] : Array.from(selectedScopes);
    if (scopes.length === 0) {
      setError("Select at least one scope or enable full access.");
      return;
    }
    // ADV-998 — A key with only the inbound-webhook scope is a separate
    // namespace (`adva_inb_*`). Surface that to the API as `kind: 'inbound'`
    // so the backend mints the right prefix.
    const isInboundOnly =
      !useWildcard && scopes.length === 1 && scopes[0] === "inbound:write";
    setSubmitting(true);
    try {
      const result = await createMutation.mutateAsync({
        name,
        scopes,
        expires_at: expires ? new Date(expires).toISOString() : undefined,
        ...(isInboundOnly ? { kind: "inbound" as const } : {}),
      });
      setCreatedRawKey(result.raw_key);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create API key");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyKey(key: string) {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy. Select and copy manually.");
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[512px] sm:max-w-[512px] flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {createdRawKey ? "API Key Created" : "Create API Key"}
          </SheetTitle>
        </SheetHeader>

        {createdRawKey ? (
          <div className="flex-1 overflow-y-auto py-4">
            <div className="space-y-4">
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-elevated)] p-4">
                <p className="mb-3 typo-label text-[color:var(--color-text-md)]">
                  This key will only be shown once. Copy it now and store it
                  securely.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-[color:var(--color-surface)] px-3 py-2 typo-mono text-sm text-[color:var(--color-text-hi)]">
                    {createdRawKey}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyKey(createdRawKey)}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-[color:var(--color-success)]" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form
            id="create-key-form"
            className="flex-1 overflow-y-auto py-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <SettingsField label="Name">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Production Import Key"
              />
            </SettingsField>

            <SettingsField label="Permissions">
              <div
                className={cn(
                  "rounded-md border p-3 transition-colors",
                  useWildcard
                    ? "border-[color:var(--color-border)] bg-[color:var(--color-elevated)]"
                    : "border-[color:var(--color-border)]",
                )}
              >
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={useWildcard}
                    onCheckedChange={(checked: boolean) => {
                      setUseWildcard(!!checked);
                      if (checked) setSelectedScopes(new Set());
                    }}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                      Full access
                    </p>
                    <p className="typo-body text-xs text-[color:var(--color-text-md)]">
                      Inherits your role — can do what you can do, nothing more.
                    </p>
                  </div>
                </label>
              </div>

              {!useWildcard && (
                <div className="mt-3 space-y-3">
                  {visibleGroups.map((group) => (
                    <div
                      key={group.key}
                      className="rounded-md border border-[color:var(--color-border)] p-3"
                    >
                      <p className="typo-body font-medium text-[color:var(--color-text-hi)] mb-1">
                        {group.label}
                      </p>
                      <p className="typo-body text-xs text-[color:var(--color-text-md)] mb-2">
                        {group.description}
                      </p>
                      {group.kind === "rw" ? (
                        <div className="flex flex-wrap gap-2">
                          {[
                            {
                              scope: group.readScope,
                              label: "Read",
                            },
                            {
                              scope: group.writeScope,
                              label: "Write",
                            },
                          ].map(({ scope, label }) => (
                            <label
                              key={scope}
                              className={cn(
                                "inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 typo-body text-sm transition-colors",
                                selectedScopes.has(scope)
                                  ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/10 text-[color:var(--color-text-hi)]"
                                  : "border-[color:var(--color-border)] text-[color:var(--color-text-md)] hover:bg-[color:var(--color-elevated)]",
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={selectedScopes.has(scope)}
                                onChange={() => toggleScope(scope)}
                                className="sr-only"
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <label
                          className={cn(
                            "inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 typo-body text-sm transition-colors",
                            selectedScopes.has(group.scope)
                              ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/10 text-[color:var(--color-text-hi)]"
                              : "border-[color:var(--color-border)] text-[color:var(--color-text-md)] hover:bg-[color:var(--color-elevated)]",
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selectedScopes.has(group.scope)}
                            onChange={() => toggleScope(group.scope)}
                            className="sr-only"
                          />
                          Enable
                        </label>
                      )}
                    </div>
                  ))}
                  <p className="typo-body text-xs text-[color:var(--color-text-lo)]">
                    Scopes only narrow — they never grant more than your role
                    allows.
                  </p>
                </div>
              )}
            </SettingsField>

            <SettingsField label="Expiration" description="Optional">
              <Input
                type="datetime-local"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            </SettingsField>

            {error && (
              <p className="typo-body text-sm text-destructive">{error}</p>
            )}
          </form>
        )}

        {createdRawKey ? (
          <SheetFooter className="border-t pt-4">
            <Button type="button" onClick={handleClose}>
              Done
            </Button>
          </SheetFooter>
        ) : (
          <FormSheetFooter
            onCancel={handleClose}
            submitLabel="Create Key"
            isSubmitting={submitting || !name.trim()}
            formId="create-key-form"
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Edit Name Sheet ──────────────────────────────────────────────────────────

interface EditApiKeySheetProps {
  apiKey: ApiKey | null;
  onClose: () => void;
}

function EditApiKeySheet({ apiKey, onClose }: EditApiKeySheetProps) {
  const [name, setName] = useState(apiKey?.name ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateMutation = useUpdateApiKey();

  useEffect(() => {
    if (apiKey) setName(apiKey.name);
  }, [apiKey?.id]);

  async function handleSubmit() {
    if (!apiKey) return;
    setError(null);
    setSubmitting(true);
    try {
      await updateMutation.mutateAsync({ id: apiKey.id, name });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={!!apiKey} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-[400px] sm:max-w-[400px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Rename API Key</SheetTitle>
        </SheetHeader>
        <form
          id="edit-key-form"
          className="flex-1 py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <SettingsField label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </SettingsField>
          {error && (
            <p className="typo-body text-sm text-destructive">{error}</p>
          )}
        </form>
        <FormSheetFooter
          onCancel={onClose}
          submitLabel="Save"
          isSubmitting={submitting || !name.trim()}
          formId="edit-key-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function V2SettingsApiKeysPage() {
  const ability = useAbility();
  const { data, isLoading } = useApiKeys();
  const revokeMutation = useRevokeApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [revokingKey, setRevokingKey] = useState<ApiKey | null>(null);

  const apiKeys = data?.api_keys ?? [];

  async function handleRevokeConfirm() {
    if (!revokingKey) return;
    await revokeMutation.mutateAsync(revokingKey.id);
    setRevokingKey(null);
  }

  return (
    <SettingsPage
      title="API Keys"
      subtitle="Programmatic access for integrations and automations."
      actions={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          Create API Key
        </Button>
      }
    >
      <SettingsTable
        colSpan={7}
        isLoading={isLoading}
        isEmpty={apiKeys.length === 0}
        emptyMessage="No API keys yet. Create one to get started."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Key
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Scopes
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Status
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Last Used
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Created
            </TableHead>
            <TableHead className="w-20 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {apiKeys.map((key) => (
            <TableRow
              key={key.id}
              className="h-9 border-b border-[color:var(--color-border)] last:border-0"
            >
              <TableCell className="px-4 py-1.5">
                <div className="flex items-center gap-2">
                  <Key className="h-3.5 w-3.5 text-[color:var(--color-text-lo)]" />
                  <span className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    {key.name}
                  </span>
                </div>
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <code className="rounded bg-[color:var(--color-surface)] px-2 py-0.5 typo-mono text-xs text-[color:var(--color-text-md)]">
                  adva_k_{key.key_prefix}…
                </code>
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <div className="flex flex-wrap gap-1">
                  {key.scopes.map((scope) => (
                    <Badge
                      key={scope}
                      variant={scope === "*" ? "default" : "secondary"}
                      className={
                        scope === "*"
                          ? "bg-[color:var(--color-elevated)] text-[color:var(--color-text-hi)] border-[color:var(--color-border)]"
                          : ""
                      }
                    >
                      {scope === "*" ? "Full access" : scope}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <Badge variant={key.is_active ? "default" : "outline"}>
                  {key.is_active ? "Active" : "Revoked"}
                </Badge>
              </TableCell>
              <TableCell className="px-4 py-1.5 hidden lg:table-cell typo-body text-xs text-[color:var(--color-text-lo)]">
                {key.last_used_at
                  ? formatRelativeDate(key.last_used_at)
                  : "Never"}
              </TableCell>
              <TableCell className="px-4 py-1.5 hidden lg:table-cell typo-body text-xs text-[color:var(--color-text-lo)]">
                {new Date(key.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="px-4 py-1.5">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    title="Edit name"
                    onClick={() => setEditingKey(key)}
                  >
                    <Pencil className="h-3.5 w-3.5 text-[color:var(--color-text-md)]" />
                  </Button>
                  {key.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 hover:bg-destructive/10"
                      title="Revoke key"
                      onClick={() => setRevokingKey(key)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SettingsTable>

      <CreateApiKeySheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        isAdmin={ability.can("update", "Account")}
      />

      <EditApiKeySheet
        apiKey={editingKey}
        onClose={() => setEditingKey(null)}
      />

      <ConfirmDialog
        open={!!revokingKey}
        onOpenChange={(open) => !open && setRevokingKey(null)}
        title="Revoke API key?"
        description={`Revoke "${revokingKey?.name}"? Any integrations using this key will immediately lose access. This cannot be undone.`}
        confirmLabel="Revoke"
        onConfirm={handleRevokeConfirm}
      />
    </SettingsPage>
  );
}
